/**
 * URL contract of the Real Estate Asset Management page (GEN-701 QA feedback):
 * the page carries the selected REA and the active tab in its search params —
 * `/real-estate-asset-management?reaId=<uuid>&tab=<tabKey>` — so any view is
 * deep-linkable and the routed Steckbrief-Variant form can navigate back to the
 * exact REA + tab it was opened from. `tab` values are the page's tab keys:
 * the static tabs verbatim plus `group:<code>` for data-collection group tabs;
 * `general` is the default and is never written to the URL.
 */

export type StaticReaTab = 'general' | 'services' | 'steckbriefe' | 'history';
export type ReaTab = StaticReaTab | { kind: 'group'; code: string };

const STATIC_REA_TABS: StaticReaTab[] = ['general', 'services', 'steckbriefe', 'history'];
const GROUP_TAB_PREFIX = 'group:';

export function isGroupTab(tab: ReaTab): tab is { kind: 'group'; code: string } {
    return typeof tab !== 'string';
}

/** Serialized form of a tab — MUI Tabs `value` and the `?tab=` param share it. */
export function tabKey(tab: ReaTab): string {
    return isGroupTab(tab) ? `${GROUP_TAB_PREFIX}${tab.code}` : tab;
}

/** Inverse of {@link tabKey}; unknown/absent keys fall back to `general`. */
export function parseReaTab(raw: string | null): ReaTab {
    if (!raw) return 'general';
    if (raw.startsWith(GROUP_TAB_PREFIX)) {
        const code = raw.slice(GROUP_TAB_PREFIX.length);
        return code ? { kind: 'group', code } : 'general';
    }
    return STATIC_REA_TABS.includes(raw as StaticReaTab) ? (raw as StaticReaTab) : 'general';
}

/** The REA management page with an REA (and optionally a non-default tab) selected. */
export function reaPageUrl(reaId: string, tab: ReaTab = 'general'): string {
    const params = new URLSearchParams({ reaId });
    const key = tabKey(tab);
    if (key !== 'general') params.set('tab', key);
    return `/real-estate-asset-management?${params.toString()}`;
}

/** The routed "new Steckbrief Variant" form page (GEN-701 QA feedback #1). */
export function steckbriefVariantNewUrl(reaId: string, steckbriefId: string): string {
    return `/real-estate-asset-management/${reaId}/steckbriefe/${steckbriefId}/variants/new`;
}

/**
 * The routed Steckbrief-Variant form page opened ON an existing Variant
 * (GEN-765): the form is prefilled from that Variant's data stamp and saving
 * creates a NEW Variant (Variants stay immutable). Sibling of the `new` route —
 * the static `new` segment outranks the `:variantId` param in route matching.
 */
export function steckbriefVariantOpenUrl(reaId: string, steckbriefId: string, variantId: string): string {
    return `/real-estate-asset-management/${reaId}/steckbriefe/${steckbriefId}/variants/${variantId}`;
}
